import type { SavedReport } from '@clockwise/clients/report-definitions';
import type { ReportLabel } from '@clockwise/clients/report-labels';
import { MAX_REPORT_DESCRIPTION, MAX_REPORT_NAME } from '@clockwise/report-kit';
import { ApiError } from '@clockwise/shared';
import { type UseQueryResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/custom/confirm-dialog';
import { NameDescriptionDialog } from '@/components/custom/name-description-dialog';
import { SearchInput } from '@/components/custom/search-input';
import { shareReach } from '@/components/custom/share-badge';
import { ShortcutHint } from '@/components/custom/shortcut-hint';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { ShareReportDialog } from '@/components/reports/share-report-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { reportDefinitionsApi, reportLabelsApi } from '@/lib/api';
import { apiErrorText } from '@/lib/errors';
import { useMe } from '@/lib/me';
import { queryKeys } from '@/lib/query-keys';
import { LabelNameDialog } from './label-name-dialog';
import { LabelRail } from './label-rail';
import {
  buildEntries,
  type LibraryView,
  MAX_SEARCH,
  offersSharedByYou,
  railLabels,
  viewCounts,
  visibleEntries,
} from './library-model';
import { ReportRow } from './report-row';
import { useReportLabels } from './use-library';

type SavedList = { data: SavedReport[]; meta: { total: number } };

interface Props {
  saved: UseQueryResult<SavedList>;
  view: LibraryView;
  q: string;
  /** Write the view and search back to the URL, so every state is a link. */
  onSearch: (next: { view?: LibraryView; q?: string }) => void;
}

const SEARCH_ID = 'reports-search';

const FIXED_TITLES: Record<string, string> = {
  all: 'All reports',
  starred: 'Starred',
  mine: 'Created by you',
  'shared-by-you': 'Shared by you',
  shared: 'Shared with you',
};

/**
 * The Reports page, organised by labels. See docs/reporting/report-labels.md.
 *
 * Two requests feed it — the saved reports (up to 200, as before) and the
 * caller's labels — and everything else is computed here: what each view
 * holds, what the search leaves, and every count in the rail. The filtering
 * runs in the browser over that one page (the G13 exception recorded in
 * docs/decisions.md); the model's rules live in `library-model.ts`.
 */
export function ReportsLibrary({ saved, view, q, onSearch }: Props) {
  const { permissions } = useMe();
  const canEdit = permissions.has('report.edit');
  const canShare = permissions.has('report.share');
  const canManageStandard = permissions.has('report.label');
  const labelsQuery = useReportLabels();
  const queryClient = useQueryClient();

  // `/` jumps to the search box from anywhere on the page — the shortcut most
  // list pages on the web have taught people. Not while typing in a field.
  // By id rather than a ref: SearchInput keeps its own ref for the clear
  // button, and a second one passed through its props would replace it.
  useHotkeys(
    '/',
    (e) => {
      e.preventDefault();
      document.getElementById(SEARCH_ID)?.focus();
    },
    // Matched on the CHARACTER typed, not the physical key: `/` sits on a
    // different key on many layouts, and the library matches `event.code` by
    // default.
    { useKey: true },
  );

  const labels = useMemo(() => labelsQuery.data?.data.labels ?? [], [labelsQuery.data]);
  const entries = useMemo(
    () => buildEntries(saved.data?.data ?? [], labelsQuery.data?.data.items ?? []),
    [saved.data, labelsQuery.data],
  );
  // A link to a label that has since been deleted falls back to every report
  // rather than an empty page with no explanation.
  const known =
    !view.startsWith('label:') || labels.some((l) => `label:${l.id}` === view) || !labelsQuery.data;
  const effectiveView: LibraryView = known ? view : 'all';
  // Memoised: these walk every report once per view, and the page re-renders
  // on every keystroke, hover and optimistic tick.
  const shown = useMemo(
    () => visibleEntries(entries, effectiveView, q, labels),
    [entries, effectiveView, q, labels],
  );
  const counts = useMemo(() => viewCounts(entries, q, labels), [entries, q, labels]);
  const rail = useMemo(
    () => railLabels(labels, entries, canManageStandard),
    [labels, entries, canManageStandard],
  );
  const title =
    FIXED_TITLES[effectiveView] ??
    labels.find((l) => `label:${l.id}` === effectiveView)?.name ??
    'Reports';

  // ---- Label dialogs -------------------------------------------------------
  const [creating, setCreating] = useState<'personal' | 'standard' | null>(null);
  const [renaming, setRenaming] = useState<ReportLabel | null>(null);
  const [deletingLabel, setDeletingLabel] = useState<ReportLabel | null>(null);
  const [labelError, setLabelError] = useState<string | null>(null);
  const refreshLabels = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.reportLabels.all });

  const createLabel = useMutation({
    mutationFn: (v: { name: string; kind: 'personal' | 'standard' }) => reportLabelsApi.create(v),
    onSuccess: ({ data }) => {
      setCreating(null);
      onSearch({ view: `label:${data.id}` });
      return refreshLabels();
    },
    onError: (err) => setLabelError(apiErrorText(err, 'Couldn’t create the label.')),
  });
  const renameLabel = useMutation({
    mutationFn: (v: { id: string; name: string }) => reportLabelsApi.rename(v.id, v.name),
    onSuccess: () => {
      setRenaming(null);
      return refreshLabels();
    },
    onError: (err) => setLabelError(apiErrorText(err, 'Couldn’t rename the label.')),
  });
  const deleteLabel = useMutation({
    mutationFn: (id: string) => reportLabelsApi.remove(id),
    onSuccess: (_d, id) => {
      setDeletingLabel(null);
      if (view === `label:${id}`) onSearch({ view: 'all' });
      return refreshLabels();
    },
    onError: (err) => toast.error(apiErrorText(err, 'Couldn’t delete the label.')),
  });

  // ---- Report actions ------------------------------------------------------
  const [sharing, setSharing] = useState<SavedReport | null>(null);
  const [renamingReport, setRenamingReport] = useState<SavedReport | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [deletingReport, setDeletingReport] = useState<SavedReport | null>(null);
  const refreshReports = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.reportDefinitions.all }),
      refreshLabels(),
    ]);

  const duplicate = useMutation({
    mutationFn: (r: SavedReport) => reportDefinitionsApi.duplicate(r.id),
    onSuccess: ({ data }) => {
      toast.success(`Saved “${data.name}”`);
      return refreshReports();
    },
    onError: (err) => toast.error(apiErrorText(err, 'Couldn’t save a copy.')),
  });
  const renameReport = useMutation({
    mutationFn: (v: { id: string; name: string; description: string }) =>
      reportDefinitionsApi.patch(v.id, { name: v.name, description: v.description }),
    onSuccess: () => {
      setRenamingReport(null);
      return refreshReports();
    },
    onError: (err) => setReportError(apiErrorText(err, 'Couldn’t rename the report.')),
  });
  const deleteReport = useMutation({
    mutationFn: (id: string) => reportDefinitionsApi.remove(id),
    onSuccess: () => {
      setDeletingReport(null);
      return refreshReports();
    },
    onError: (err) => toast.error(apiErrorText(err, 'Couldn’t delete the report.')),
  });

  const total = saved.data?.meta.total ?? 0;
  const truncated = total > (saved.data?.data.length ?? 0);
  const loading = saved.isPending || labelsQuery.isPending;
  const failed = saved.isError ? saved.error : labelsQuery.isError ? labelsQuery.error : null;

  return (
    <PageShell>
      <PageHeader title="Reports" subtitle="Find, run and organize reports.">
        {canEdit && (
          <Button asChild>
            <Link to="/reports/new">
              <Plus className="size-4" aria-hidden />
              New report
            </Link>
          </Button>
        )}
      </PageHeader>

      <div className="grid grid-cols-[14rem_minmax(0,1fr)] gap-10">
        <LabelRail
          view={effectiveView}
          // Also while it is the view on screen — a link straight to it must
          // not leave the rail with nothing selected.
          sharedByYou={offersSharedByYou(entries, canShare) || effectiveView === 'shared-by-you'}
          onView={(v) => onSearch({ view: v })}
          counts={counts}
          personal={rail.personal}
          standard={rail.standard}
          canManageStandard={canManageStandard}
          onCreate={(kind) => {
            setLabelError(null);
            setCreating(kind);
          }}
          onRename={(l) => {
            setLabelError(null);
            setRenaming(l);
          }}
          onDelete={setDeletingLabel}
        />

        <div className="min-w-0 space-y-4">
          {/* The list's own header: what you are looking at on the left,
              search on the right at the app's usual search width — a box the
              full width of the list reads as the page's main control. */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-baseline gap-2">
              <h2 className="truncate font-semibold text-lg">{title}</h2>
              {!loading && !failed && (
                // The count alone — the heading already says what is counted,
                // and the search box shows what it was narrowed by.
                <span className="shrink-0 text-muted-foreground text-sm tabular-nums">
                  ({shown.length})
                </span>
              )}
            </div>
            <div className="relative shrink-0">
              <SearchInput
                id={SEARCH_ID}
                value={q}
                onValueChange={(v) => onSearch({ q: v })}
                placeholder="Search reports"
                aria-label="Search reports"
                aria-keyshortcuts="/"
                maxLength={MAX_SEARCH}
              />
              {!q && (
                <ShortcutHint keys={['/']} className="absolute top-1/2 right-2 -translate-y-1/2" />
              )}
            </div>
          </div>

          {/* G15: a list that failed to load must not read as an empty one. */}
          {failed ? (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/5 px-3 py-2 text-sm"
            >
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
              <p className="min-w-0">
                <span className="font-medium text-destructive">Couldn’t load your reports.</span>{' '}
                {failed instanceof ApiError ? failed.message : 'Please try again.'}
                {failed instanceof ApiError && failed.traceId ? (
                  <span className="text-muted-foreground"> (trace {failed.traceId})</span>
                ) : null}{' '}
                <button
                  type="button"
                  className="underline"
                  onClick={() => {
                    void saved.refetch();
                    void labelsQuery.refetch();
                  }}
                >
                  Retry
                </button>
              </p>
            </div>
          ) : loading ? (
            <div className="space-y-2" role="status" aria-busy="true" aria-label="Loading reports">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : shown.length === 0 ? (
            <EmptyState
              q={q}
              view={effectiveView}
              canShare={canShare}
              onClear={() => onSearch({ q: '' })}
              onAll={() => onSearch({ view: 'all' })}
            />
          ) : (
            // The same white table card every other list page sits its rows
            // in, with the shared table-row lime hover on each row.
            <ul data-surface="table" className="overflow-hidden rounded-md border light:bg-card">
              {shown.map((entry) => (
                <ReportRow
                  key={entry.key}
                  entry={entry}
                  labels={labels}
                  q={q}
                  canEdit={canEdit}
                  canShare={canShare}
                  onShare={setSharing}
                  // One copy per click: a double activation would otherwise
                  // save "(copy)" and "(copy 2)".
                  onDuplicate={(r) => {
                    if (!duplicate.isPending) duplicate.mutate(r);
                  }}
                  onRename={(r) => {
                    setReportError(null);
                    setRenamingReport(r);
                  }}
                  onDelete={setDeletingReport}
                />
              ))}
            </ul>
          )}

          {truncated && !loading && (
            <p className="text-muted-foreground text-sm">
              Showing {saved.data?.data.length} of {total} saved reports. Search covers only the
              ones shown — ask for server-side search if you need the rest.
            </p>
          )}
        </div>
      </div>

      <LabelNameDialog
        open={creating !== null}
        onOpenChange={(o) => !o && setCreating(null)}
        title={creating === 'standard' ? 'New standard label' : 'New label'}
        description={
          creating === 'standard'
            ? 'Everyone sees standard labels, on the reports already shared with them.'
            : 'Only you see your labels.'
        }
        initialName=""
        submitLabel="Create label"
        error={labelError}
        busy={createLabel.isPending}
        onSubmit={(name) => creating && createLabel.mutate({ name, kind: creating })}
      />
      <LabelNameDialog
        open={renaming !== null}
        onOpenChange={(o) => !o && setRenaming(null)}
        title="Rename label"
        description={
          renaming?.kind === 'standard' ? 'Renames it for everyone.' : 'Only you see your labels.'
        }
        initialName={renaming?.name ?? ''}
        submitLabel="Rename"
        error={labelError}
        busy={renameLabel.isPending}
        onSubmit={(name) => renaming && renameLabel.mutate({ id: renaming.id, name })}
      />
      <ConfirmDialog
        open={deletingLabel !== null}
        title={`Delete label “${deletingLabel?.name ?? ''}”?`}
        description={
          deletingLabel?.kind === 'standard'
            ? 'It disappears from everyone’s list. The reports under it are not deleted.'
            : 'The reports under it are not deleted.'
        }
        confirmLabel="Delete label"
        destructive
        confirmDisabled={deleteLabel.isPending}
        onConfirm={() => deletingLabel && deleteLabel.mutate(deletingLabel.id)}
        onCancel={() => setDeletingLabel(null)}
      />

      {sharing && (
        <ShareReportDialog report={sharing} open onOpenChange={(o) => !o && setSharing(null)} />
      )}
      {renamingReport && (
        <NameDescriptionDialog
          open
          onOpenChange={(o) => !o && !renameReport.isPending && setRenamingReport(null)}
          heading="Rename report"
          blurb={
            renamingReport.shares.length > 0
              ? 'Changes show for everyone it’s shared with.'
              : 'This is how you’ll find it again.'
          }
          suggestion={renamingReport.name}
          description={renamingReport.description ?? ''}
          maxName={MAX_REPORT_NAME}
          maxDescription={MAX_REPORT_DESCRIPTION}
          error={reportError}
          busy={renameReport.isPending}
          onSubmit={(name, description) =>
            renameReport.mutate({ id: renamingReport.id, name, description })
          }
          submitLabel="Save"
        />
      )}
      <ConfirmDialog
        open={deletingReport !== null}
        title={`Delete “${deletingReport?.name ?? ''}”?`}
        description={
          deletingReport && deletingReport.shares.length > 0
            ? `This report is shared with ${shareReach(deletingReport.shares)}. Deleting it removes it for all of them, and from every label it is in. This can’t be undone.`
            : 'It’s removed from every label it is in. This can’t be undone.'
        }
        confirmLabel="Delete report"
        destructive
        confirmDisabled={deleteReport.isPending}
        onConfirm={() => deletingReport && deleteReport.mutate(deletingReport.id)}
        onCancel={() => setDeletingReport(null)}
      />
    </PageShell>
  );
}

/** "No matches" and "nothing here yet" are different sentences (G15). */
function EmptyState({
  q,
  view,
  canShare,
  onClear,
  onAll,
}: {
  q: string;
  view: LibraryView;
  /** Whether to say HOW to share — only to someone the menu offers Share to. */
  canShare: boolean;
  onClear: () => void;
  onAll: () => void;
}) {
  const message = q
    ? `No reports match “${q}”.`
    : view === 'starred'
      ? 'Nothing starred yet. Hover a report and click the star.'
      : view === 'mine'
        ? 'You haven’t saved a report yet.'
        : view === 'shared-by-you'
          ? canShare
            ? 'You haven’t shared a report yet. Open a report’s menu and choose Share.'
            : 'You haven’t shared a report.'
          : view === 'shared'
            ? 'Nobody has shared a report with you yet.'
            : view.startsWith('label:')
              ? 'Nothing under this label yet. Hover a report and pick the tag to add it.'
              : 'No reports yet.';
  return (
    <div className="rounded-lg border border-dashed bg-card px-4 py-8 text-center text-muted-foreground text-sm">
      <p>{message}</p>
      {q ? (
        <button type="button" className="mt-2 underline" onClick={onClear}>
          Clear search
        </button>
      ) : view !== 'all' ? (
        <button type="button" className="mt-2 underline" onClick={onAll}>
          See all reports
        </button>
      ) : null}
    </div>
  );
}
